import { useQuery } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import { getPrivateInstance } from "../_privateInstance";

export type FinancialsDepositsByPlatformFilters = {
  start_date: string;
  end_date: string;
};

export type FinancialsDepositPlatformSlice = {
  platformId: string;
  name: string;
  depositAmount: number;
  /** Null when the API omits percentage; UI derives only in that case. */
  percentage: number | null;
  color: string;
};

type RawPlatformRow = {
  platform_id?: string | null;
  name?: string | null;
  deposit_amount?: number | string | null;
  percentage?: number | string | null;
};

const PLATFORM_COLORS = [
  "#2ECC71",
  "#F39C12",
  "#F1C40F",
  "#3498DB",
  "#1F4690",
  "#9B59B6",
  "#E74C3C",
  "#1ABC9C",
  "#E67E22",
  "#95A5A6",
] as const;

const coerceNumber = (value: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const parsePercentage = (value: unknown): number | null => {
  if (value == null || value === "") return null;
  return coerceNumber(value);
};

/** Stable chart color per platform so reordering API rows does not change hues. */
export const platformColorForId = (platformId: string): string => {
  let hash = 0;
  for (let i = 0; i < platformId.length; i += 1) {
    hash = (hash * 31 + platformId.charCodeAt(i)) >>> 0;
  }
  return PLATFORM_COLORS[hash % PLATFORM_COLORS.length];
};

/** Maps the deposits-by-platform HTTP envelope into chart-ready slices. */
export const parseFinancialsDepositsByPlatformResponse = (
  body: unknown,
): FinancialsDepositPlatformSlice[] => {
  const rawList = Array.isArray((body as { data?: unknown })?.data)
    ? ((body as { data: RawPlatformRow[] }).data ?? [])
    : [];

  return rawList.map((row) => {
    const name =
      typeof row?.name === "string" && row.name
        ? row.name
        : String(row?.platform_id ?? "");
    const platformId =
      typeof row?.platform_id === "string" && row.platform_id
        ? row.platform_id
        : name;

    return {
      platformId,
      name,
      depositAmount: coerceNumber(row.deposit_amount),
      percentage: parsePercentage(row.percentage),
      color: platformColorForId(platformId),
    };
  });
};

export const fetchFinancialsDepositsByPlatform = async (
  filters: FinancialsDepositsByPlatformFilters,
): Promise<FinancialsDepositPlatformSlice[]> => {
  const instance = getPrivateInstance();
  const response = await instance.get(
    API_ROUTES.FINANCIALS_DEPOSITS_BY_PLATFORM,
    { params: filters },
  );
  return parseFinancialsDepositsByPlatformResponse(response?.data);
};

export const useGetFinancialsDepositsByPlatform = (
  filters: FinancialsDepositsByPlatformFilters,
) => {
  const { start_date, end_date } = filters;

  return useQuery<FinancialsDepositPlatformSlice[], Error>({
    queryKey: ["FINANCIALS_DEPOSITS_BY_PLATFORM", start_date, end_date],
    queryFn: () => fetchFinancialsDepositsByPlatform(filters),
    enabled: Boolean(start_date && end_date),
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
};
